import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismIcon,
  PrismInput,
  PrismLoadingBar,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { AiUsage, Review } from 'lumen/models';
import type { ReplyOverrides } from 'lumen/models/reviews/review';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { ReviewDraft } from 'lumen/services/review-draft-cache';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { ApiError } from 'lumen/utils/get-api-error-message';

import styles from './response.module.css';

type AiReplyRequestPayload = {
  external_id: string;
  language_code: string;
  ota_id: string;
  property_id: string;
  review_id: string;
  review_text: string;
  score: number;
};

type AiReplyResponsePayload = {
  generated_at: string;
  generated_reply: string;
  id: string;
} & AiReplyRequestPayload;

type AiImproveReplyRequestPayload = {
  external_id: string;
  language_code: string;
  llm_generation_id: number | null;
  ota_id: string;
  property_id: string;
  response_text: string;
  review_id: string;
  review_text: string;
  score: number;
};

type AiImproveReplyResponsePayload = {
  id: string;
  improved_at: string;
  improved_reply: string;
} & AiImproveReplyRequestPayload;

const RATING_VALUES = {
  GOOD: 'GOOD',
  BAD: 'BAD',
} as const;

const QUOTA_EXCEEDED_ERROR_CODE = 'upsell.quota.exceeded';
const TRIAL_ALREADY_SETUP_ERROR_CODE = 'trial_already_requested';
const ALREADY_RESPONDED_ERROR_CODE = 'ota.reply.already_responded';

type RatingParameters = (typeof RATING_VALUES)[keyof typeof RATING_VALUES];

interface HotelReviewsReviewDetailResponseSignature {
  Args: {
    aiUsageData: AiUsage | null;
    onAiUsageChange?: () => void;
    review: Review;
  };
}

export default class HotelReviewsReviewDetailResponse extends Component<HotelReviewsReviewDetailResponseSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare reviewDraftCache: Services['review-draft-cache'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @service declare usageAnalytics: Services['usage-analytics'];

  // Textarea & generation buttons behavior:
  // EMPTY: textareaValue empty → Show "Generate"
  // GENERATED: isResponseGenerated=true, hasUserEditedResponse=false → Show rating buttons
  // RATED: generatedReplyRating set → Replace buttons with static rated icons
  // EDITED: hasUserEditedResponse=true → Show "Improve", hide ratings
  @tracked generatedReplyRating: RatingParameters | null = null;
  @tracked hasUserEditedResponse = false;
  @tracked isReplyTextareaTouched = false;
  @tracked isResponseGenerated = false;
  // Intentionally ephemeral — resets on navigation. Re-clicking "Contact Sales"
  // will show a toast if the trial is already being set up.
  @tracked isUpsellRequested = false;
  @tracked showProcessedReply = false;
  @tracked textareaValue = '';

  // Store the last review ID to detect changes
  lastReviewId?: string;

  resetState = modifier((_element, [review]: [Review]): void => {
    if (this.lastReviewId !== review.id) {
      this.isReplyTextareaTouched = false;
      this.showProcessedReply = false;

      // Restore draft for the new review if it exists, otherwise clear state
      // Note: drafts are saved eagerly in onTextAreaInput and after AI generation
      const existingDraft = this.reviewDraftCache.getDraft(review.id!);
      if (existingDraft) {
        this.restoreDraftState(existingDraft);
      } else {
        this.clearResponseState();
      }

      this.lastReviewId = review.id!;
    }
  });

  generateReviewResponseTask = task(async (): Promise<void> => {
    const { review } = this.args;

    const postBody: AiReplyRequestPayload = {
      review_id: review.id!,
      ota_id: review.otaId,
      external_id: review.externalId,
      review_text: this.reviewText,
      score: review.score,
      language_code: this.hotelAndMarketService.user.locale,
      property_id: review.propertyId!,
    };

    const url = `${this.ajax.host}/${APIGATEWAY}/review-manager/ai/reply/generate?subscription_id=${this.hotelAndMarketService.hotel.id!}`;

    const response: AiReplyResponsePayload = await this.ajax.post(url, {
      data: JSON.stringify(postBody),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload({
      'reviews/ai-reply': response,
    });

    this.usageAnalytics.track('AI Reply Generated', {
      eventTrigger: 'review-detail-card',
      product: 'review-manager',
      actionType: 'generate',
    });
  });

  improveReviewResponseTask = task(async (): Promise<void> => {
    const { review } = this.args;

    const postBody: AiImproveReplyRequestPayload = {
      llm_generation_id: review.lastAiReply?.id
        ? parseInt(review.lastAiReply.id, 10)
        : null,
      review_id: review.id!,
      ota_id: review.otaId,
      external_id: review.externalId,
      review_text: this.reviewText,
      property_id: review.propertyId!,
      response_text: this.textareaValue,
      score: review.score,
      language_code: this.hotelAndMarketService.user.locale,
    };

    const url = `${this.ajax.host}/${APIGATEWAY}/review-manager/ai/reply/improve?subscription_id=${this.hotelAndMarketService.hotel.id!}`;

    const response: AiImproveReplyResponsePayload = await this.ajax.post(url, {
      data: JSON.stringify(postBody),
      dataType: 'json',
      contentType: 'application/json; charset=utf-8',
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.store.pushPayload({
      'reviews/ai-reply': response,
    });

    this.usageAnalytics.track('AI Reply Generated', {
      eventTrigger: 'review-detail-card',
      product: 'review-manager',
      actionType: 'improve',
    });
  });

  requestUpsellTrialTask = task({ drop: true }, async () => {
    const requestUrl = formatUrl(
      `/${APIGATEWAY}/channel-management/upsell/`,
      { subscription_id: this.hotelAndMarketService.hotel.id! },
      this.ajax.host,
    );

    try {
      await this.ajax.post(requestUrl);
      return { isSuccess: true, isTrialPending: false };
    } catch (error: unknown) {
      if (this.isTrialAlreadyRequestedError(error)) {
        return { isSuccess: false, isTrialPending: true };
      }
      void this.alerting.showErrorAlert();
      this.errorTracking.captureException(error);
      return { isSuccess: false, isTrialPending: false };
    }
  });

  sendGeneratedReviewReplyRateTask = task(
    { drop: true },
    async (
      generatedReplyId: string,
      rating: RatingParameters,
    ): Promise<void> => {
      if (!generatedReplyId) {
        return;
      }

      const previousRating = this.generatedReplyRating;

      // Optimistically update UI
      this.generatedReplyRating = rating;
      this.saveDraftToCache();

      try {
        const url = `${this.ajax.host}/${APIGATEWAY}/review-manager/ai/reply/${generatedReplyId}/rate?subscription_id=${this.hotelAndMarketService.hotel.id!}`;

        await this.ajax.post(url, {
          data: JSON.stringify({ rating }),
          dataType: 'json',
          contentType: 'application/json; charset=utf-8',
        });

        this.usageAnalytics.track('AI Reply Rated', {
          eventTrigger: 'review-detail-card',
          product: 'review-manager',
          rating,
          generatedReplyId,
        });
      } catch (error) {
        // Revert optimistic update
        this.generatedReplyRating = previousRating;
        this.saveDraftToCache();

        void this.alerting.showErrorAlert();
        this.errorTracking.captureException(error);
      }
    },
  );

  sendResponseTask = task(
    { drop: true },
    async (replyText: string): Promise<void> => {
      const { review } = this.args;

      try {
        const overrides: ReplyOverrides = {
          ota_review_reply: replyText,
        };

        if (this.isResponseGenerated && review.lastAiReply) {
          overrides.llm_generation_id = parseInt(review.lastAiReply.id!, 10);
        }

        await review.save(this.hotelAndMarketService.hotel.id!, overrides);

        // Clear the draft from cache since it was successfully sent
        this.reviewDraftCache.clearDraft(review.id!);

        this.usageAnalytics.track('Review Answer Sent', {
          eventTrigger: 'review-detail-card',
          product: 'review-manager',
          reviewRating: review.score,
          channelId: review.otaId,
          isAIGenerated: this.isResponseGenerated,
          hasAIGeneratedReplies: review.lastAiReply ? true : false,
        });

        this.clearResponseState();
      } catch (error) {
        if (this.isAlreadyRespondedError(error)) {
          void this.alerting.showErrorAlert({
            title: this.intl.t('review_manager.error.already_replied.title'),
            text: this.intl.t('review_manager.error.already_replied.text'),
          });
        } else {
          void this.alerting.showErrorAlert();
        }
        this.errorTracking.captureException(error);
      }
    },
  );

  get displayedReplyText(): string {
    if (this.showProcessedReply && this.hasProcessedReply) {
      return this.args.review.processedOtaReviewReply;
    }
    return this.args.review.isPendingApproval
      ? this.args.review.lhReviewReply
      : this.args.review.otaReviewReply;
  }

  get errorMessage(): string {
    if (this.isTextAreaEmpty) {
      return this.intl.t('review_manager.review_detail.empty_reply');
    }

    return '';
  }

  get hasErrors(): boolean {
    if (this.sendResponseTask.isRunning || this.isGeneratingResponse) {
      return false;
    }

    return this.isTextAreaEmpty;
  }

  get hasNoGenerationsLeft(): boolean {
    return (
      this.access.hasReviewManagerReplyGenerateUpsell &&
      this.args.aiUsageData?.unlimited === false &&
      this.args.aiUsageData?.amountOfGenerations === 0
    );
  }

  get hasProcessedReply(): boolean {
    const {
      isPendingApproval,
      lhReviewReply,
      otaReviewReply,
      processedOtaReviewReply,
    } = this.args.review;
    const replyToCompare = isPendingApproval ? lhReviewReply : otaReviewReply;
    return (
      !!processedOtaReviewReply &&
      processedOtaReviewReply.trim() !== '' &&
      processedOtaReviewReply !== replyToCompare
    );
  }

  get isGenerateButtonDisabled(): boolean {
    return (
      !this.access.hasReviewManagerReplyGenerate || this.hasNoGenerationsLeft
    );
  }

  get isGeneratingResponse(): boolean {
    return (
      this.generateReviewResponseTask.isRunning ||
      this.improveReviewResponseTask.isRunning
    );
  }

  get isSendResponseButtonDisabled(): boolean {
    return (
      !this.access.hasReviewManagerReplyPush ||
      this.sendResponseTask.isRunning ||
      this.isGeneratingResponse
    );
  }

  get isTextAreaDisabled(): boolean {
    return (
      (!this.access.hasReviewManagerReplyPush &&
        !this.access.hasReviewManagerReplyGenerate) ||
      this.isGeneratingResponse
    );
  }

  get isTextAreaEmpty(): boolean {
    return this.textareaValue.trim().length === 0;
  }

  get isThumbsDownRatingActive(): boolean {
    return this.generatedReplyRating === RATING_VALUES.BAD;
  }

  get isThumbsUpRatingActive(): boolean {
    return this.generatedReplyRating === RATING_VALUES.GOOD;
  }

  get reviewText(): string {
    const { review } = this.args;

    return review.content || `${review.positive} | ${review.negative}`;
  }

  get shouldShowGenerateButton(): boolean {
    return this.isTextAreaEmpty;
  }

  get shouldShowImproveButton(): boolean {
    return (
      !this.isTextAreaEmpty &&
      (!this.isResponseGenerated || this.hasUserEditedResponse)
    );
  }

  get shouldShowRatingFeedback(): boolean {
    return this.isResponseGenerated && !this.hasUserEditedResponse;
  }

  get shouldShowReplyTextarea(): boolean {
    return !this.args.review.isPendingApproval && !this.args.review.hasReply;
  }

  get showAiUsageCounter(): boolean {
    return (
      this.access.hasReviewManagerReplyGenerateUpsell &&
      this.args.aiUsageData?.unlimited === false &&
      !this.shouldShowRatingFeedback
    );
  }

  get showUpsellBanner(): boolean {
    return (
      this.hasNoGenerationsLeft &&
      // Prevents users who already started a trial from seeing the upsell banner again
      !this.access.hasReviewManagerReplyGenerateUpsellTrialRequested
    );
  }

  get thumbsDownIconColor(): 'on-info' | 'subdued' {
    return this.isThumbsDownRatingActive ? 'on-info' : 'subdued';
  }

  get thumbsDownIconName(): 'thumbs-down' | 'thumbs-down-filled' {
    return this.isThumbsDownRatingActive ? 'thumbs-down-filled' : 'thumbs-down';
  }

  get thumbsUpIconColor(): 'on-info' | 'subdued' {
    return this.isThumbsUpRatingActive ? 'on-info' : 'subdued';
  }

  get thumbsUpIconName(): 'thumbs-up' | 'thumbs-up-filled' {
    return this.isThumbsUpRatingActive ? 'thumbs-up-filled' : 'thumbs-up';
  }

  get upsellBannerPrimaryAction(): {
    action: () => void;
    disabled: boolean;
    isLoading: boolean;
    label: string;
  } {
    const isRunning = this.requestUpsellTrialTask.isRunning;
    return {
      action: () => void this.onContactSalesClick(),
      disabled: isRunning,
      isLoading: isRunning,
      label: this.intl.t('review_manager.upsell_banner.cta'),
    };
  }

  private handleAiTaskError(error: unknown): void {
    if (this.isQuotaExceededError(error)) {
      void this.alerting.showErrorAlert({
        title: this.intl.t('review_manager.error.quota_exceeded.title'),
        text: this.intl.t('review_manager.upsell_message.text'),
      });
    } else {
      void this.alerting.showErrorAlert();
    }
    this.errorTracking.captureException(error);
  }

  private isAlreadyRespondedError(error: unknown): boolean {
    return (
      (error as ApiError)?.payload?.errors?.[0]?.code ===
      ALREADY_RESPONDED_ERROR_CODE
    );
  }

  private isQuotaExceededError(error: unknown): boolean {
    return (
      (error as ApiError)?.payload?.errors?.[0]?.code ===
      QUOTA_EXCEEDED_ERROR_CODE
    );
  }

  private isTrialAlreadyRequestedError(error: unknown): boolean {
    return (
      (error as ApiError)?.payload?.errors?.[0]?.code ===
      TRIAL_ALREADY_SETUP_ERROR_CODE
    );
  }

  async performAiTaskAndUpdateState(
    aiTask: typeof this.generateReviewResponseTask,
    replyProperty: 'generatedReply' | 'improvedReply',
  ): Promise<boolean> {
    if (this.isGeneratingResponse) {
      return false;
    }

    try {
      await aiTask.perform();
    } catch (error) {
      this.handleAiTaskError(error);
      return false;
    }

    if (this.args.review.lastAiReply) {
      this.textareaValue = this.args.review.lastAiReply[replyProperty];
      this.isResponseGenerated = true;
      this.generatedReplyRating = null;
      this.hasUserEditedResponse = false;

      // Save draft after AI generation
      this.saveDraftToCache();
    }

    return true;
  }

  private restoreDraftState(draft: ReviewDraft): void {
    this.hasUserEditedResponse = draft.hasUserEditedResponse;
    this.generatedReplyRating = draft.generatedReplyRating;
    this.isResponseGenerated = draft.isResponseGenerated;
    this.textareaValue = draft.textareaValue;
  }

  private saveDraftToCache(): void {
    const reviewId = this.args.review.id;
    if (reviewId && !this.isTextAreaEmpty) {
      this.reviewDraftCache.setDraft(reviewId, {
        hasUserEditedResponse: this.hasUserEditedResponse,
        generatedReplyRating: this.generatedReplyRating,
        isResponseGenerated: this.isResponseGenerated,
        textareaValue: this.textareaValue,
      });
    } else if (reviewId) {
      // Clear the draft if textarea is empty
      this.reviewDraftCache.clearDraft(reviewId);
    }
  }

  @action
  clearResponseState(): void {
    if (this.generateReviewResponseTask.isRunning) {
      void this.generateReviewResponseTask.cancelAll();
    }
    if (this.improveReviewResponseTask.isRunning) {
      void this.improveReviewResponseTask.cancelAll();
    }

    this.generatedReplyRating = null;
    this.isResponseGenerated = false;
    this.hasUserEditedResponse = false;
    this.showProcessedReply = false;
    this.textareaValue = '';
  }

  @action
  async onContactSalesClick(): Promise<void> {
    const result = await this.requestUpsellTrialTask.perform();
    if (result.isSuccess) {
      this.isUpsellRequested = true;
    } else if (result.isTrialPending) {
      this.toast.alert(this.intl.t('review_manager.upsell_toast.pending'), {
        showIcon: true,
      });
    }
  }

  @action
  async onGenerateResponseClick(): Promise<void> {
    const success = await this.performAiTaskAndUpdateState(
      this.generateReviewResponseTask,
      'generatedReply',
    );

    if (success && this.access.hasReviewManagerReplyGenerateUpsell) {
      this.args.onAiUsageChange?.();
    }
  }

  @action
  async onImproveResponseClick(): Promise<void> {
    const success = await this.performAiTaskAndUpdateState(
      this.improveReviewResponseTask,
      'improvedReply',
    );

    if (success && this.access.hasReviewManagerReplyGenerateUpsell) {
      this.args.onAiUsageChange?.();
    }
  }

  @action
  onRateReplyGeneration(rating: RatingParameters): void {
    const lastAiReply = this.args.review.lastAiReply;
    if (lastAiReply?.id) {
      void this.sendGeneratedReviewReplyRateTask.perform(
        lastAiReply.id,
        rating,
      );
    }
  }

  @action
  onSendResponseClick(): void {
    if (this.sendResponseTask.isRunning || this.isGeneratingResponse) {
      return;
    }

    this.isReplyTextareaTouched = true;

    if (this.isTextAreaEmpty) {
      return;
    }

    void this.sendResponseTask.perform(this.textareaValue);
  }

  @action
  onTextAreaBlur(): void {
    // Note: added to finalise the behavior that textarea errors are reset when switching between selected reviews
    this.isReplyTextareaTouched = true;
  }

  @action
  onTextAreaInput(event: Event): void {
    this.textareaValue = (event.target as HTMLTextAreaElement).value;

    this.hasUserEditedResponse = true;

    if (this.isResponseGenerated) {
      this.isResponseGenerated = false;
      this.generatedReplyRating = null;
    }

    // Save draft eagerly so it's available when switching reviews
    this.saveDraftToCache();
  }

  @action
  onToggleProcessedReply(): void {
    this.showProcessedReply = !this.showProcessedReply;
  }

  <template>
    <section class="pt-800" {{this.resetState @review}}>
      <div class="flex h-600 items-center justify-between">
        <p class="text-300--bold">{{t
            "review_manager.review_detail.title.response"
          }}</p>

        {{#if @review.isPendingApproval}}
          <span class="flex items-center gap-200">
            <p class="text-200--regular">{{t
                "review_manager.review_detail.sent_on"
                date=@review.formattedReplySentDate
                time=@review.formattedReplySentTime
              }}</p>
            <PrismBadge
              @label={{t "review_manager.review.badge.pending_approval"}}
              @type="warning"
            />
          </span>
        {{else if @review.hasReply}}
          <span class="flex items-center gap-200">
            <p class="text-200--regular">{{t
                "review_manager.review_detail.sent_on"
                date=@review.formattedReplySentDate
                time=@review.formattedReplySentTime
              }}</p>
            {{#if @review.llmGenerationId}}
              <PrismBadge
                @iconName="automation"
                @label={{t "review_manager.review.badge.ai_generated"}}
                @type="intelligence"
              />
            {{else}}
              <PrismBadge
                @label={{t "review_manager.review.badge.manually_replied"}}
                @type="neutral"
              />
            {{/if}}
          </span>
        {{else}}
          <div class="flex items-center">
            {{#if this.showAiUsageCounter}}
              <span
                class="text-200--regular mr-200"
                data-test-id="ai-usage-counter"
              >
                {{t
                  "review_manager.ai_usage_counter.label"
                  count=@aiUsageData.amountOfGenerations
                }}
              </span>
            {{/if}}
            {{#if this.shouldShowGenerateButton}}
              <PrismButton
                @buttonType="intelligence-secondary"
                @iconBefore="artificial-intelligence"
                @isDisabled={{this.isGenerateButtonDisabled}}
                @isLoading={{this.isGeneratingResponse}}
                @label={{t "review_manager.button.generate_response.label"}}
                @onClick={{this.onGenerateResponseClick}}
                data-test-id="generate-response-button"
              >
                {{#if this.isGenerateButtonDisabled}}
                  <PrismTooltip
                    @side="top"
                    @text={{t
                      "review_manager.button.generate_response.no_access_tooltip"
                    }}
                  />
                {{/if}}
              </PrismButton>
            {{/if}}
            {{#if this.shouldShowImproveButton}}
              <PrismButton
                @buttonType="intelligence-secondary"
                @iconBefore="artificial-intelligence"
                @isDisabled={{this.isGenerateButtonDisabled}}
                @isLoading={{this.isGeneratingResponse}}
                @label={{t "review_manager.button.improve_response.label"}}
                @onClick={{this.onImproveResponseClick}}
                data-test-id="improve-response-button"
              >
                {{#if this.isGenerateButtonDisabled}}
                  <PrismTooltip
                    @side="top"
                    @text={{t
                      "review_manager.button.generate_response.no_access_tooltip"
                    }}
                  />
                {{/if}}
              </PrismButton>
            {{/if}}
            {{#if this.shouldShowRatingFeedback}}
              <span class="text-200--regular text-emphasis ml-200">{{t
                  "review_manager.improve_response_text"
                }}</span>
              <div class="ml-100 flex gap-200">
                {{#if this.generatedReplyRating}}
                  <PrismIcon
                    @color={{this.thumbsUpIconColor}}
                    @name={{this.thumbsUpIconName}}
                    @size="small"
                    {{! visually align with buttons in other rating state }}
                    class="mx-100"
                    data-test-active={{this.isThumbsUpRatingActive}}
                    data-test-id="rated-thumbs-up"
                  />
                  <PrismIcon
                    @color={{this.thumbsDownIconColor}}
                    @name={{this.thumbsDownIconName}}
                    @size="small"
                    {{! visually align with buttons in other rating state }}
                    class="mx-100"
                    data-test-active={{this.isThumbsDownRatingActive}}
                    data-test-id="rated-thumbs-down"
                  />
                {{else}}
                  <PrismButton
                    @buttonType="ghost"
                    @icon="thumbs-up"
                    @onClick={{fn
                      this.onRateReplyGeneration
                      RATING_VALUES.GOOD
                    }}
                    @size="small"
                    data-test-id="rate-thumbs-up"
                  />
                  <PrismButton
                    @buttonType="ghost"
                    @icon="thumbs-down"
                    @onClick={{fn this.onRateReplyGeneration RATING_VALUES.BAD}}
                    @size="small"
                    data-test-id="rate-thumbs-down"
                  />
                {{/if}}
              </div>
            {{/if}}
          </div>
        {{/if}}
      </div>

      {{#if @review.isPendingApproval}}
        <div class="mt-400">
          <p class="paragraph-300--regular" data-test-id="pending-reply-text">
            {{this.displayedReplyText}}
          </p>
        </div>
      {{else if @review.hasReply}}
        <div class="mt-400">
          <p class="paragraph-300--regular" data-test-id="final-reply-text">
            {{this.displayedReplyText}}
          </p>
        </div>
      {{/if}}

      {{#if this.hasProcessedReply}}
        <div class="mt-400">
          {{#if this.showProcessedReply}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="translate"
              @label={{t "review_manager.button.show_original.label"}}
              @onClick={{this.onToggleProcessedReply}}
              data-test-id="revert-processed-reply-button"
            />
          {{else}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="translate"
              @label={{t "review_manager.button.translate.label"}}
              @onClick={{this.onToggleProcessedReply}}
              data-test-id="show-processed-reply-button"
            />
          {{/if}}
        </div>
      {{/if}}

      {{#if this.shouldShowReplyTextarea}}
        {{#if this.showUpsellBanner}}
          <div class="mt-400" data-test-id="upsell-banner">
            {{#if this.isUpsellRequested}}
              <PrismBanner
                @bannerMessage={{t "review_manager.upsell_banner.pending"}}
                @bannerStyle="brand"
                @isOneLine={{true}}
              />
            {{else}}
              <PrismBanner
                @bannerMessage={{t "review_manager.upsell_message.text"}}
                @bannerStyle="brand"
                @hasCta={{true}}
                @isOneLine={{true}}
                @primaryAction={{this.upsellBannerPrimaryAction}}
              />
            {{/if}}
          </div>
        {{/if}}

        <div
          class="relative mt-400 flex w-full pr-100 pl-100
            {{if this.isResponseGenerated styles.is-generated-response}}"
        >
          {{! padding left & right above are added to not cut off the focus style of the textarea }}
          {{#if this.isGeneratingResponse}}
            {{! note: custom styling here to align loading bar with the textarea & parent div padding }}
            <PrismLoadingBar class="ml-100 w-[calc(100%-8px)]" />
          {{/if}}

          <PrismInput
            @disabled={{this.isTextAreaDisabled}}
            @errorMessage={{this.errorMessage}}
            @hasErrors={{this.hasErrors}}
            @isTouched={{this.isReplyTextareaTouched}}
            @onBlur={{this.onTextAreaBlur}}
            @onInput={{this.onTextAreaInput}}
            @placeholder={{t
              "review_manager.review_detail.textarea.placeholder"
            }}
            @rows={{10}}
            @textarea={{true}}
            @value={{this.textareaValue}}
            data-test-id="response-textarea"
          />
        </div>

        <footer class="flex justify-end">
          <PrismButton
            @buttonType="primary"
            @iconBefore="send"
            @isDisabled={{this.isSendResponseButtonDisabled}}
            @label={{t "review_manager.button.send_response.label"}}
            @onClick={{this.onSendResponseClick}}
            data-test-id="send-reply-button"
          >
            {{#unless this.access.hasReviewManagerReplyPush}}
              <PrismTooltip
                @side="top"
                @text={{t
                  "review_manager.button.send_response.no_access_tooltip"
                }}
              />
            {{/unless}}
          </PrismButton>
        </footer>
      {{/if}}
    </section>
  </template>
}
